import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import VSLogo from "@/components/VSLogo";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { api, saveAuth, loadUser } from "@/lib/api";
import { toast } from "sonner";
import { Shield, ArrowLeft, Lock } from "lucide-react";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  // If a super_admin visits /admin/login while already authenticated, skip the form.
  useEffect(() => {
    const u = loadUser();
    if (u && u.role === "super_admin") nav("/admin/dashboard", { replace: true });
  }, [nav]);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.user.role !== "super_admin") {
        // Do NOT persist auth; management portal is restricted to super_admin.
        toast.error("Management access is restricted to authorized Super Admin users.");
        return;
      }
      saveAuth(data.token, data.user);
      toast.success("Authorized access granted.");
      nav("/admin/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Authentication failed");
    } finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-6 relative overflow-hidden">
      <div className="absolute inset-0 opacity-30" style={{backgroundImage: "radial-gradient(circle at 20% 30%, rgba(245,158,11,0.15), transparent 40%), radial-gradient(circle at 80% 70%, rgba(37,99,235,0.1), transparent 40%)"}}/>
      <div className="w-full max-w-md relative">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-8" data-testid="admin-back-to-candidate">
          <ArrowLeft className="w-4 h-4"/> Back to Candidate Login
        </Link>
        <Card className="bg-slate-900 border-slate-800 p-8 overflow-hidden relative">
          <div className="absolute -right-16 -top-16 w-56 h-56 bg-[#F59E0B]/10 rounded-full blur-3xl"/>
          <div className="relative">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-[#F59E0B]/15 border border-[#F59E0B]/30 flex items-center justify-center">
                <Shield className="w-6 h-6 text-[#F59E0B]"/>
              </div>
              <div>
                <VSLogo dark size={30}/>
              </div>
            </div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#F59E0B] font-semibold mt-6">Authorized Access Only</div>
            <h1 className="font-display text-3xl font-bold mt-2 text-white">VS Management Login</h1>
            <p className="text-slate-400 mt-2 text-sm">Secure access for authorized VS-Solutions Super Admin only. HR and Recruiters — please use Candidate/Team Login.</p>
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div><Label className="text-slate-300">Management Email</Label>
                <Input type="email" required value={email} onChange={e=>setEmail(e.target.value)} className="bg-slate-800 border-slate-700 text-white" data-testid="admin-login-email"/>
              </div>
              <div><Label className="text-slate-300">Password</Label>
                <Input type="password" required value={password} onChange={e=>setPassword(e.target.value)} className="bg-slate-800 border-slate-700 text-white" data-testid="admin-login-password"/>
              </div>
              <Button disabled={loading} type="submit" className="w-full bg-[#F59E0B] text-[#0A2540] hover:bg-[#F59E0B]/90 font-semibold" data-testid="admin-login-submit">
                <Lock className="w-4 h-4 mr-2"/>{loading ? "Verifying..." : "Access Portal"}
              </Button>
            </form>
            <p className="mt-6 text-[11px] text-slate-500 leading-relaxed">
              All access is monitored and logged. Unauthorized attempts will be reported. If you are a candidate, please use the candidate login.
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}
